'use client';

import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Check, X, Upload, FileText, Calendar, Signature as SignatureIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Checkbox as CheckboxPrimitive } from '@radix-ui/react-checkbox';
import SignatureCanvas from 'react-signature-canvas';
import type { ChecklistTemplate, ChecklistItem } from '../types/checklist.types';

interface ChecklistPreviewProps {
  template: ChecklistTemplate;
  onBack: () => void;
  onComplete: () => void;
}

type ChecklistResponses = {
  [key: string]: string | boolean | number | Date | null | undefined;
};

export function ChecklistPreview({ template, onBack, onComplete }: ChecklistPreviewProps) {
  const [responses, setResponses] = useState<ChecklistResponses>({});
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const signatureRef = useRef<SignatureCanvas>(null);

  const handleResponseChange = (itemId: string, value: string | boolean | number | Date | null | undefined) => {
    // Convert Date to ISO string for storage
    const valueToStore = value instanceof Date ? value.toISOString() : value;
    setResponses(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const handleFileUpload = (itemId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    // In a real app, you would upload the file to storage and save the URL
    const file = files[0];
    console.log('Uploading file:', file.name, 'for item:', itemId);
    
    // For demo purposes, we'll just store the file name
    handleResponseChange(itemId, file.name);
  };

  const handleSignatureSave = () => {
    if (signatureRef.current) {
      const signature = signatureRef.current.toDataURL();
      setSignatureData(signature);
      setShowSignaturePad(false);
    }
  };

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignatureData(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Checklist submitted with responses:', responses);
    // In a real app, you would save the responses to your database
    onComplete();
  };

  // Helper to safely format date strings
  const formatDate = (dateValue: string | Date | null | undefined): string => {
    if (!dateValue) return '';
    try {
      const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
      return format(date, 'yyyy-MM-dd');
    } catch (e) {
      console.error('Error formatting date:', e);
      return '';
    }
  };

  const renderField = (item: ChecklistItem) => {
    const value = responses[item.id];
    const stringValue = value instanceof Date ? value.toISOString() : String(value || '');
    
    switch (item.fieldType) {
      case 'Checkbox':
        return (
          <div className="flex items-center space-x-2">
            <CheckboxPrimitive
              id={`item-${item.id}`}
              checked={!!value}
              onCheckedChange={(checked) => handleResponseChange(item.id, checked as boolean)}
              className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor={`item-${item.id}`} className="text-sm font-medium text-gray-700">
              {value ? 'Completed' : 'Mark as complete'}
            </label>
          </div>
        );

      case 'Text':
        return (
          <Textarea
            placeholder="Enter your response..."
            value={stringValue}
            onChange={(e) => handleResponseChange(item.id, e.target.value)}
            className="mt-1"
          />
        );

      case 'Number':
        return (
          <Input
            type="number"
            placeholder="Enter a number..."
            value={stringValue}
            onChange={(e) => handleResponseChange(item.id, e.target.value)}
            className="mt-1 w-32"
          />
        );

      case 'Date':
        return (
          <div className="relative mt-1">
            <Input
              type="date"
              value={formatDate(stringValue)}
              onChange={(e) => handleResponseChange(item.id, e.target.value)}
              className="w-48"
            />
            <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
        );

      case 'Signature':
        return (
          <div className="mt-2">
            {signatureData ? (
              <div className="relative">
                <img 
                  src={signatureData} 
                  alt="Signature" 
                  className="h-20 w-48 border rounded bg-white p-2"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute -right-2 -top-2 h-8 w-8 rounded-full p-0"
                  onClick={() => {
                    setSignatureData(null);
                    handleResponseChange(item.id, '');
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowSignaturePad(true)}
              >
                <SignatureIcon className="mr-2 h-4 w-4" />
                Add Signature
              </Button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderEvidenceUpload = (item: any) => {
    if (!item.requireEvidence) return null;

    return (
      <div className="mt-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Evidence
        </label>
        <div className="flex items-center">
          <label className="flex-1">
            <span className="sr-only">Choose evidence file</span>
            <Input
              type="file"
              onChange={(e) => handleFileUpload(`evidence-${item.id}`, e.target.files)}
              className="hidden"
              id={`evidence-${item.id}`}
            />
            <div className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm cursor-pointer hover:bg-gray-50">
              <span className="text-sm text-gray-700 truncate">
                {responses[`evidence-${item.id}`] instanceof Date ? responses[`evidence-${item.id}`].toISOString() : responses[`evidence-${item.id}`] || 'No file chosen'}
              </span>
              <Upload className="h-4 w-4 text-gray-500" />
            </div>
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={onBack} className="mb-4">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Editor
      </Button>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>{template.title}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
            </div>
            <Badge variant="outline" className={template.badgeClass}>
              {template.standard}
            </Badge>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {template.items.map((item) => (
                <div key={item.id} className="border rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-1">
                      <h3 className="font-medium">{item.title}</h3>
                      {item.control && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {item.control}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    {renderField(item)}
                    {renderEvidenceUpload(item)}
                  </div>
                </div>
              ))}
            </div>

            {template.items.some(item => item.fieldType === 'Signature') && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium mb-4">Signature</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Please sign below to verify the completion of this checklist.
                </p>
                
                {signatureData ? (
                  <div className="relative">
                    <img 
                      src={signatureData} 
                      alt="Signature" 
                      className="h-24 w-64 border rounded bg-white p-2"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute -right-2 -top-2 h-8 w-8 rounded-full p-0"
                      onClick={() => setSignatureData(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowSignaturePad(true)}
                  >
                    <SignatureIcon className="mr-2 h-4 w-4" />
                    Sign Here
                  </Button>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
              <Button type="submit">
                <Check className="mr-2 h-4 w-4" />
                Complete Checklist
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Signature Pad Modal */}
      {showSignaturePad && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">Sign Here</h3>
            <div className="border rounded-md">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: 'w-full h-48 bg-white',
                }}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  handleClearSignature();
                  setShowSignaturePad(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClearSignature}
              >
                Clear
              </Button>
              <Button
                type="button"
                onClick={handleSignatureSave}
              >
                Save Signature
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
